'use client';

import { FC, useCallback, useMemo } from 'react';

import { CallOffOrderColor, Color, QuotedColor } from '@ravago/shared/page-data/models/elements';
import { LeadTimeIndicator } from '@ravago/shared/page-data/models/elements/product-lead-time.model';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { ColorPicker, ColorPickerOption } from '@ravago/shared/radiance/components/input-field/components/ColorPicker/ColorPicker';
import { Dropdown, DropdownOption } from '@ravago/shared/radiance/components/input-field/components/Dropdown/Dropdown';
import { InputField } from '@ravago/shared/radiance/components/input-field/components/InputField/InputField';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { LeadTimeCheckCriteria, hasMtsLocalLeadTime } from '../../../utils/lead-time';

export interface ColorPickerValue {
   colorId: number;
   colorName: string;
   callOffOrderId: string | undefined;
   isQuotedColor: boolean;
   producerColorCode: string | undefined;
   desiredColor: string | undefined;
}

interface Props {
   id: string;
   colors: Array<Color>;
   callOffOrderColors: Array<CallOffOrderColor>;
   quotedColors: Array<QuotedColor>;
   value: ColorPickerValue | undefined;
   onChange?: (value: ColorPickerValue | undefined) => void;
   leadTimeIndicators?: LeadTimeIndicator[];
   leadTimeEnabled?: boolean;
}

export const ExtendedColorPicker: FC<Props> = ({
   id,
   colors,
   callOffOrderColors,
   quotedColors,
   value,
   onChange,
   leadTimeIndicators = [],
   leadTimeEnabled = false
}) => {
   const translate = useTranslations();
   const format = useFormatter();

   const colorOptions = useMemo(() => {
      const fromColors = colors.map((color) => ({
         id: color.id.toString(),
         label: translate(`colors.${color.name.toLowerCase().replace(' ', '-')}`),
         colorName: color.name.toLowerCase()
      }));

      const fromCallOffs = callOffOrderColors.map((color) => {
         const isOverdue = color.recommendedCallOff.status === 'Call Off Overdue';
         const priceLabel = format.unitPrice(color.recommendedCallOff.unitPrice);
         const badges = [{ label: priceLabel, variant: 'info' }];
         if (isOverdue) {
            badges.unshift({ label: translate('extended-color-picker.overdue'), variant: 'error' });
         }

         let label = translate(`colors.${color.color.name.toLowerCase().replace(' ', '-')}`);
         if (color.color.producerColorCode) label = `${label} ${color.color.producerColorCode}`;

         return {
            id: color.recommendedCallOff.id,
            label,
            colorName: color.color.name.toLowerCase(),
            badges
         };
      });

      const fromQuotedColors = quotedColors.map((quotedColor) => {
         const lowestPriceLine = quotedColor.quotations
            .flatMap((quotation) => quotation.lines)
            .reduce((lowest, line) => (line.unitPrice.price.amount < lowest.unitPrice.price.amount ? line : lowest));
         const priceLabel = format.unitPrice(lowestPriceLine.unitPrice);
         const badges = [{ label: priceLabel, variant: 'info' }];

         let label = translate(`colors.${quotedColor.color.name.toLowerCase().replace(' ', '-')}`);
         if (quotedColor.color.producerColorCode) label = `${label} ${quotedColor.color.producerColorCode}`;

         return {
            id: `${quotedColor.color.id.toString()}-${quotedColor.color.producerColorCode}-quoted`,
            label,
            colorName: quotedColor.color.name.toLowerCase(),
            badges
         };
      });

      return [...fromCallOffs, ...fromQuotedColors, ...fromColors];
   }, [colors, callOffOrderColors, quotedColors, translate]);

   const selectedColor = useMemo(() => {
      if (!value) return undefined;
      if (value.callOffOrderId) return colorOptions.find((option) => option.id === value.callOffOrderId);
      if (value.isQuotedColor) return colorOptions.find((option) => option.id === `${value.colorId}-${value.producerColorCode}-quoted`);
      if (value.colorId) return colorOptions.find((option) => option.id === value.colorId?.toString());
      return undefined;
   }, [value, colorOptions]);

   const colorCodeMtsLocalMap = useMemo(() => {
      if (!leadTimeEnabled || !value?.colorId || !selectedColor || selectedColor.id === '-1') return {};

      const selectedColorCodes = colors.find((col) => col.id.toString() === selectedColor.id)?.producerColorCodes;
      if (!selectedColorCodes) return {};

      return Object.fromEntries(
         selectedColorCodes.map((producerColorCode) => {
            const criteria: LeadTimeCheckCriteria = {
               colorId: value.colorId,
               producerColorCode: producerColorCode.colorCode
            };
            return [producerColorCode.colorCode, hasMtsLocalLeadTime(leadTimeIndicators, criteria)];
         })
      );
   }, [selectedColor, colors, leadTimeIndicators, value, leadTimeEnabled]);

   const colorCodeOptions: DropdownOption[] = useMemo(() => {
      if (!selectedColor || selectedColor.id === '-1') return [];

      const selectedColorCodes = colors.find((col) => col.id.toString() === selectedColor.id)?.producerColorCodes;
      if (!selectedColorCodes) return [];

      const isAllCustomLeadTime = Object.values(colorCodeMtsLocalMap).every((colorCodeMtsLocal) => !colorCodeMtsLocal);

      const selectedColorCodesOptions = selectedColorCodes.map((producerColorCode) => {
         const option: DropdownOption = {
            id: producerColorCode.colorCode,
            value: producerColorCode.colorCode
         };

         if (leadTimeEnabled && !isAllCustomLeadTime) {
            option.badge = {
               label:
                  colorCodeMtsLocalMap[producerColorCode.colorCode] === true
                     ? translate('request-for-quotation.lead-time.mts-local-days')
                     : translate('request-for-quotation.lead-time.delivery-on-request'),
               variant: 'info',
               leadingIcon: 'truck'
            };
         }

         return option;
      });

      const otherOption: DropdownOption = { id: 'Other', value: 'Other' };

      if (leadTimeEnabled && !isAllCustomLeadTime) {
         // For "Other", it's always delivery on request since it's a custom color code
         otherOption.badge = {
            label: translate('request-for-quotation.lead-time.delivery-on-request'),
            variant: 'info',
            leadingIcon: 'truck'
         };
      }

      return [...selectedColorCodesOptions, otherOption];
   }, [selectedColor, colors, colorCodeMtsLocalMap, leadTimeEnabled, translate]);

   const selectedColorCode = useMemo(() => {
      if (!value) return undefined;
      if (value.producerColorCode) return colorCodeOptions.find((option) => option.id === value.producerColorCode);
      return undefined;
   }, [value, colorCodeOptions]);

   const desiredColor = value?.desiredColor ?? '';

   const disabled = colorOptions.length === 1;

   const showColorCode = value?.colorId && value.colorId !== -1 && !value.callOffOrderId && !value.isQuotedColor;
   const showOtherField = value?.colorId === -1 || value?.producerColorCode === 'Other';

   const callOffIds = callOffOrderColors.map((color) => color.recommendedCallOff.id);
   const quotedColorIds = quotedColors.map((color) => `${color.color.id}-${color.color.producerColorCode}-quoted`);
   const colorIds = colors.map((color) => color.id.toString());

   const groupByFn = useCallback(
      (option: ColorPickerOption) => {
         if (callOffIds.includes(option.id)) return translate('extended-color-picker.call-off-colors');
         if (quotedColorIds.includes(option.id)) return translate('extended-color-picker.order-from-quote');
         if (colorIds.includes(option.id)) return translate('extended-color-picker.request-new-quote');
         return '';
      },
      [callOffIds, quotedColorIds, colorIds, translate]
   );

   const handleColorChange = (option: ColorPickerOption | undefined) => {
      if (!option) {
         onChange?.(undefined);
         return;
      }

      if (callOffIds.includes(option.id)) {
         const callOffOrderColor = callOffOrderColors.find((color) => color.recommendedCallOff.id === option.id);
         if (!callOffOrderColor) return;
         onChange?.({
            colorId: callOffOrderColor.color.id,
            colorName: translate(`colors.${callOffOrderColor.color.name.toLowerCase().replace(' ', '-')}`),
            callOffOrderId: callOffOrderColor.recommendedCallOff.id,
            isQuotedColor: false,
            producerColorCode: callOffOrderColor.color.producerColorCode,
            desiredColor: undefined
         });
      }

      if (option.id.includes('-quoted')) {
         const quotedColor = quotedColors.find((color) => `${color.color.id.toString()}-${color.color.producerColorCode}-quoted` === option.id);
         if (!quotedColor) return;
         onChange?.({
            colorId: quotedColor.color.id,
            colorName: translate(`colors.${quotedColor.color.name.toLowerCase().replace(' ', '-')}`),
            callOffOrderId: undefined,
            isQuotedColor: true,
            producerColorCode: quotedColor.color.producerColorCode,
            desiredColor: undefined
         });
      }

      if (colorIds.includes(option.id)) {
         const foundColor = colors.find((color) => color.id.toString() === option.id);
         if (!foundColor) return;
         onChange?.({
            colorId: foundColor.id,
            colorName: translate(`colors.${foundColor.name.toLowerCase().replace(' ', '-')}`),
            callOffOrderId: undefined,
            isQuotedColor: false,
            producerColorCode: undefined,
            desiredColor: undefined
         });
      }
   };

   const handleColorCodeChange = (option: DropdownOption | undefined, colorPreferences = '') => {
      if (!value) return;
      onChange?.({ ...value, producerColorCode: option?.value, desiredColor: colorPreferences });
   };

   const handleDesiredColorChange = (newDesiredColor: string) => {
      if (!value) return;
      onChange?.({ ...value, desiredColor: newDesiredColor });
   };

   return (
      <Box gap="lg" direction="column">
         <ColorPicker
            id={`${id}-color-picker`}
            disabled={disabled}
            label={translate('extended-color-picker.label')}
            options={colorOptions}
            value={selectedColor}
            onChange={handleColorChange}
            groupByFn={callOffOrderColors.length > 0 || quotedColors.length ? groupByFn : undefined}
         />
         {showColorCode && (
            <Dropdown
               id={`${id}-color-code-dropdown`}
               label={`${translate('extended-color-picker.color-code-label')} (${translate(
                  'extended-color-picker.desired-color-support-text'
               ).toLowerCase()})`}
               value={selectedColorCode}
               onChange={handleColorCodeChange}
               options={colorCodeOptions}
               clearable
               onClear={() => handleColorCodeChange(undefined)}
               emptyOption={false}
            />
         )}
         {showOtherField && (
            <InputField
               id={`${id}-other-input`}
               label={translate('extended-color-picker.desired-color-label')}
               value={desiredColor}
               maxLength={50}
               onChange={handleDesiredColorChange}
               supportText={translate('extended-color-picker.desired-color-support-text')}
               trailingIcon="droplet"
            />
         )}
      </Box>
   );
};
